import { useState, type ChangeEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  IMAGE_MAX_BYTES,
  base64FromDataUrl,
  readFileAsDataUrl,
  validateImageFile,
} from '@/lib/image-file';
import { useUploadTenantLogo } from './use-tenant';

/**
 * Logo del negocio (Ajustes → General): subir un archivo desde el computador del admin o pegar la
 * dirección de una imagen que ya está publicada.
 *
 * La subida es un endpoint aparte (`POST .../logo`, ver `useUploadTenantLogo`) que devuelve la URL
 * del archivo ya guardado en el backend; esa URL entra al formulario y es la que se persiste al
 * pulsar «Guardar cambios» (el `PATCH` del negocio). Por eso descartar el formulario NO cambia el
 * logo: el archivo queda en disco sin usar, pero volver a subirlo reemplaza ese mismo archivo
 * (`replaceSiblings` en `backend/src/lib/local-storage.ts`), así que no se acumulan copias.
 *
 * Se valida ANTES de leer (tamaño y MIME declarado): un archivo de 20 MB no se lee para nada y el
 * usuario ve el motivo al instante. El formato real lo confirma el backend por firma binaria.
 *
 * La vista previa la pinta la tarjeta (`Avatar` con el nombre del negocio): es el mismo `Avatar` que
 * usa el selector de negocio del shell, así que lo que se ve aquí es lo que se verá allí.
 */
export interface SettingsLogoFieldProps {
  /** `logoUrl` del formulario (la guardada, o la que acaba de devolver la subida). */
  value: string;
  onChange: (logoUrl: string) => void;
  /** `true` sin rol ADMIN: las dos rutas (`PATCH` y `POST .../logo`) exigen ADMIN. */
  disabled?: boolean;
  /** Mensaje de validación del formulario (se pinta bajo el campo de la URL). */
  error?: string;
}

export function SettingsLogoField({
  value,
  onChange,
  disabled = false,
  error,
}: SettingsLogoFieldProps) {
  const { tenantId } = useActiveTenantAccess();
  const uploadLogo = useUploadTenantLogo(tenantId);

  const [uploading, setUploading] = useState(false);
  /** Error de la última subida (validación del cliente o mensaje del backend). */
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** Cambiar la `key` permite volver a elegir el MISMO archivo después de quitarlo. */
  const [fileInputKey, setFileInputKey] = useState(0);

  const ocupado = disabled || uploading;
  const maxMb = Math.round(IMAGE_MAX_BYTES / 1024 / 1024);

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setFileInputKey((key) => key + 1);
    if (!file) return;

    const fileError = validateImageFile(file);
    if (fileError) {
      setUploadError(fileError);
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      const { base64, mimeType } = base64FromDataUrl(await readFileAsDataUrl(file));
      const saved = await uploadLogo.mutateAsync({
        imageBase64: base64,
        ...(mimeType ? { mimeType } : {}),
      });
      onChange(saved.url);
    } catch (err) {
      setUploadError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-4">
      {uploadError ? <Alert tone="danger">{uploadError}</Alert> : null}

      <Field
        label="Subir el logo desde tu computador"
        htmlFor="logoArchivo"
        hint={`PNG, JPG, WEBP, GIF o HEIC de hasta ${maxMb} MB. Se sube al elegirlo y se guarda al pulsar «Guardar cambios»; si lo subes otra vez, reemplaza al anterior.`}
      >
        <Input
          key={fileInputKey}
          id="logoArchivo"
          name="logoArchivo"
          type="file"
          accept="image/*"
          disabled={ocupado}
          onChange={(event) => void handleFile(event)}
        />
      </Field>

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="…o pegar la dirección de una imagen (URL)"
          htmlFor="logoUrl"
          className="min-w-64 flex-1"
          hint="Ej. https://tunegocio.com/logo.png"
          error={error}
        >
          <Input
            id="logoUrl"
            name="logoUrl"
            type="url"
            placeholder="https://…"
            value={value}
            disabled={ocupado}
            invalid={Boolean(error)}
            aria-describedby={error ? 'logoUrl-error' : undefined}
            onChange={(event) => onChange(event.target.value)}
          />
        </Field>

        <Button
          variant="ghost"
          size="sm"
          disabled={ocupado || value.trim().length === 0}
          onClick={() => {
            setUploadError(null);
            onChange('');
          }}
        >
          Quitar el logo
        </Button>
      </div>

      {uploading ? (
        <p className="text-caption text-ink-muted" role="status">
          Subiendo el logo…
        </p>
      ) : (
        <p className="text-micro text-ink-faint">
          El logo aparece en el selector de negocio. Si no carga, se ven las iniciales del nombre; sin
          logo, el bot sigue funcionando igual (el logo es solo del panel).
        </p>
      )}
    </div>
  );
}